var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
//src/core/components/shop/Product/AllProductsPage/index.tsx
import { useEffect, useState, useMemo } from "react";
import { fetchProducts } from "@/core/api/stripe";
import SingleProductCard from "./SingleProductCard";
import DonationModal from "../ui/DonationModal";
import { useCart } from "@/core/context/CartContext";
const transformStripeProduct = (stripeProduct) => {
    var _a, _b, _c, _d, _e, _f, _g;
    return {
        id: stripeProduct.id,
        imageUrl: stripeProduct.image || stripeProduct.images[0] || "",
        image: stripeProduct.image || stripeProduct.images[0] || "", // Add this line
        title: stripeProduct.name,
        name: stripeProduct.name,
        description: "",
        slug: stripeProduct.name.toLowerCase().replace(/\s+/g, "-"),
        price: stripeProduct.price || "",
        weight: ((_a = stripeProduct.meta) === null || _a === void 0 ? void 0 : _a.Weight) || "",
        weightType: ((_b = stripeProduct.meta) === null || _b === void 0 ? void 0 : _b.WeightUnit) || "",
        colors: [(_c = stripeProduct.meta) === null || _c === void 0 ? void 0 : _c.Color].filter(Boolean),
        meta: {
            Color: ((_d = stripeProduct.meta) === null || _d === void 0 ? void 0 : _d.Color) || "",
            Type: ((_e = stripeProduct.meta) === null || _e === void 0 ? void 0 : _e.Type) || "Other",
            Weight: ((_f = stripeProduct.meta) === null || _f === void 0 ? void 0 : _f.Weight) || "",
            WeightUnit: ((_g = stripeProduct.meta) === null || _g === void 0 ? void 0 : _g.WeightUnit) || "",
        },
    };
};
const AllProductsPage = () => {
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState("Thermal Coffee Cup");
    const [selectedColor, setSelectedColor] = useState("");
    const [selectedWeight, setSelectedWeight] = useState("");
    const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const { dispatch } = useCart();
    // Get unique product types for filter options
    const productTypes = useMemo(() => {
        const types = Array.from(new Set(allProducts.map((p) => p.meta.Type)));
        return types
            .filter((type) => type && type !== "Donation" && type !== "Other")
            .sort();
    }, [allProducts]);
    // Get unique colors and weights for the selected type
    const colors = useMemo(() => {
        if (selectedType !== "Thermal Coffee Cup")
            return [];
        const typeProducts = allProducts.filter((p) => p.meta.Type === selectedType);
        const uniqueColors = Array.from(new Set(typeProducts.flatMap((p) => p.colors)));
        return uniqueColors.filter(Boolean).sort();
    }, [allProducts, selectedType]);
    const weights = useMemo(() => {
        if (selectedType !== "Thermal Coffee Cup")
            return [];
        const typeProducts = allProducts.filter((p) => p.meta.Type === selectedType);
        const uniqueWeights = Array.from(new Set(typeProducts.map((p) => p.weight)));
        return uniqueWeights.filter(Boolean).sort();
    }, [allProducts, selectedType]);
    // Filter products based on selected type, color, and weight
    const filteredProducts = useMemo(() => {
        let products = allProducts;
        if (selectedType) {
            products = products.filter((product) => product.meta.Type === selectedType);
        }
        if (selectedColor) {
            products = products.filter((product) => product.colors.includes(selectedColor));
        }
        if (selectedWeight) {
            products = products.filter((product) => product.weight === selectedWeight);
        }
        return products;
    }, [allProducts, selectedType, selectedColor, selectedWeight]);
    useEffect(() => {
        const getProducts = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const stripeProducts = yield fetchProducts();
                const transformedProducts = stripeProducts.map(transformStripeProduct);
                setAllProducts(transformedProducts);
            }
            catch (err) {
                setError(err instanceof Error ? err.message : "Failed to fetch products");
            }
            finally {
                setLoading(false);
            }
        });
        getProducts();
    }, []);
    // Reset color and weight filters when selectedType changes
    useEffect(() => {
        setSelectedColor("");
        setSelectedWeight("");
    }, [selectedType]);
    const handleProductClick = (product) => {
        if (product.meta.Type === "Donation") {
            setSelectedProduct(product);
            setIsDonationModalOpen(true);
        }
        else {
            dispatch({
                type: "ADD_ITEM",
                payload: Object.assign(Object.assign({}, product), { quantity: 1, selectedColor: product.colors[0] }),
            });
        }
    };
    if (loading) {
        return (_jsx("div", { className: "flex items-center justify-center min-h-screen", children: _jsx("div", { className: "text-xl", children: "Loading..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: "flex items-center justify-center min-h-screen", children: _jsx("div", { className: "text-xl text-red-600", children: error }) }));
    }
    return (_jsxs("section", { children: [_jsxs("div", { className: "w-11/12 max-w-6xl text-[#17303b] py-6 mx-auto lato-bold", children: [_jsx("div", { children: _jsx("h1", { className: "text-3xl font-bold ", children: "All Products" }) }), _jsx("div", { children: _jsxs("div", { className: "flex flex-col items-start w-full gap-4 py-5 md:justify-start md:flex-wrap md:flex-row", children: [_jsxs("div", { className: "flex flex-row items-center w-full gap-4 md:w-fit", children: [_jsx("label", { htmlFor: "productType", className: "w-max", children: "Filter by Type:" }), _jsxs("select", { id: "productType", value: selectedType, onChange: (e) => setSelectedType(e.target.value), className: "p-2 border rounded-md min-w-48", children: [_jsx("option", { value: "", children: "All Products" }), productTypes.map((type) => (_jsx("option", { value: type, children: type }, type)))] })] }), selectedType === "Thermal Coffee Cup" && (_jsxs("div", { className: "flex flex-col items-center gap-4 md:flex-row", children: [_jsxs("div", { className: "flex flex-row items-center justify-between w-full gap-4 md:w-auto", children: [_jsx("label", { htmlFor: "productColor", className: " w-max", children: "Filter by Color:" }), _jsxs("select", { id: "productColor", value: selectedColor, onChange: (e) => setSelectedColor(e.target.value), className: "p-2 border rounded-md min-w-48", children: [_jsx("option", { value: "", children: "All Colors" }), colors.map((color) => (_jsx("option", { value: color, children: color }, color)))] })] }), _jsxs("div", { className: "flex flex-row items-center justify-between w-full gap-4 md:w-fit", children: [_jsx("label", { htmlFor: "productWeight", className: " w-max", children: "Filter by Weight:" }), _jsxs("select", { id: "productWeight", value: selectedWeight, onChange: (e) => setSelectedWeight(e.target.value), className: "p-2 border rounded-md min-w-48", children: [_jsx("option", { value: "", children: "All Weights" }), weights.map((weight) => (_jsxs("option", { value: weight, children: [weight, " oz"] }, weight)))] })] })] }))] }) }), _jsx("div", { className: "grid grid-cols-1 gap-4 md:grid-cols-3", children: filteredProducts.map((product) => (_jsx(SingleProductCard, { product: product, onClick: () => handleProductClick(product) }, product.id))) }), filteredProducts.length === 0 && (_jsx("div", { className: "mt-8 text-center text-gray-600", children: "No products found for the selected filter." }))] }), isDonationModalOpen && selectedProduct && (_jsx(DonationModal, { product: selectedProduct, onClose: () => setIsDonationModalOpen(false) }))] }));
};
export default AllProductsPage;
